import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEntitlements } from '@utils/providers/entitlements-context.tsx';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import { FeatureFlags } from '@utils/types/entitlements.ts';
import { PATHS } from '../../router/paths.ts';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { Lock, ArrowLeft, Sparkles } from 'lucide-react';

type FeatureGuardProps = {
  /** The feature flag to check */
  feature: keyof FeatureFlags;
  /** The children to render if the feature is enabled */
  children: React.ReactNode;
};

/**
 * Guards a page/component based on feature entitlements.
 * Shows a "feature not available" message if the feature is disabled.
 */
export default function FeatureGuard({ feature, children }: FeatureGuardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasFeature, isLoading } = useEntitlements();
  const { data: organization } = useOrganization();
  const { getColour } = useThemeContext();

  // While loading, show nothing to avoid flash
  if (isLoading) {
    return null;
  }

  // If feature is enabled, render children
  if (hasFeature(feature)) {
    return <>{children}</>;
  }

  // Feature is disabled - show blocked message
  const isResellee = organization?.is_resellee;

  return (
    <div
      className="flex flex-col items-center justify-center min-h-[60vh] px-4"
      style={{ color: getColour('normalText') }}
    >
      <div
        className="flex flex-col items-center text-center max-w-md p-8 rounded-2xl border"
        style={{
          backgroundColor: getColour('secondLayer'),
          borderColor: getColour('border')
        }}
      >
        {/* Icon */}
        <div
          className="flex items-center justify-center w-16 h-16 rounded-full mb-6"
          style={{ backgroundColor: getColour('thirdLayer') }}
        >
          <Lock className="w-8 h-8 text-amber-500" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold mb-3">
          {t('feature-not-available') || 'Feature Not Available'}
        </h1>

        {/* Description */}
        <p
          className="text-sm mb-6"
          style={{ color: getColour('inactiveText') }}
        >
          {t('feature-not-available-description') ||
            'This feature is not available on your current plan.'}
        </p>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 w-full">
          <Button
            onClick={() => navigate(PATHS().HOME)}
            label={t('back-to-dashboard') || 'Back to Dashboard'}
            variant="smoke"
            size="medium"
            icon={<ArrowLeft className="w-4 h-4" />}
            className="flex-1"
          />

          {/* Only show upgrade button if not a resellee */}
          {!isResellee && (
            <Button
              onClick={() => navigate(PATHS().SUBSCRIBE)}
              label={t('upgrade-plan') || 'Upgrade Plan'}
              variant="primary"
              size="medium"
              icon={<Sparkles className="w-4 h-4" />}
              className="flex-1"
            />
          )}
        </div>
      </div>
    </div>
  );
}
